import { createBrowserHistory } from 'history';
import { setAutoFreeze } from 'immer';
import { createReduxHistoryContext } from 'redux-first-history';
import { reducer as form } from 'redux-form';
import { Middleware, combineReducers, configureStore } from '@reduxjs/toolkit';
import type { Action, AsyncThunkAction, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { loadState } from '../api/localStorage';
import articles from '../slices/articles';
import beacons from '../slices/beacons';
import clusters from '../slices/clusters';
import colors from '../slices/colors';
import crops from '../slices/crops';
import devices from '../slices/devices';
import equipments from '../slices/equipments';
import equipmentSessionObservations from '../slices/observations/equipmentSessions';
import equipmentObservations from '../slices/observations/equipments';
import generalObservations from '../slices/observations/general';
import parcelObservations from '../slices/observations/parcels';
import taskObservations from '../slices/observations/tasks';
import parcels from '../slices/parcels';
import partner from '../slices/partner';
import phyto from '../slices/phyto';
import sessions from '../slices/sessions';
import snacks from '../slices/snacks';
import tags from '../slices/tags';
import taskTypes from '../slices/taskTypes';
import tasks from '../slices/tasks';
import ui from '../slices/ui';
import unitLocations from '../slices/unitLocations';
import user from '../slices/users';
import workOrders from '../slices/workOrders';
import worksites from '../slices/worksites';

// Disable immer's store freezing in production to avoid crashes
setAutoFreeze(import.meta.env.MODE !== 'production');

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

const throwMiddleware: Middleware = () => (next) => (action) => {
  // Redux Toolkit's default behavior is to silence thunk errors and report them in "rejected" actions.
  // Let reducers process the rejected action first, then rethrow so callers' try/catch still fire.
  const result = next(action);
  const { error, payload, meta } = action as {
    error?: unknown;
    payload?: unknown;
    meta?: { rejectedWithValue?: boolean };
  };
  if (error) {
    // `error` went through miniSerializeError, which only keeps name/message/stack/code. A thunk that
    // needs to hand a richer object to its caller rejects with a value, left intact in `payload`.
    throw meta?.rejectedWithValue ? payload : error;
  }
  return result;
};

const store = configureStore({
  reducer: {
    articles,
    beacons,
    colors,
    clusters,
    crops,
    devices,
    equipments,
    form,
    observations: combineReducers({
      general: generalObservations,
      equipments: equipmentObservations,
      parcels: parcelObservations,
      equipmentsSessions: equipmentSessionObservations,
      tasks: taskObservations,
    }),
    parcels,
    partner,
    phyto,
    router: routerReducer,
    sessions,
    snacks,
    tags,
    tasks,
    taskTypes,
    ui,
    unitLocations,
    user,
    workOrders,
    worksites,
  },
  preloadedState: loadState(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(routerMiddleware, throwMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;

// throwMiddleware above rethrows on any rejected action, so an awaited thunk dispatch only
// ever yields the fulfilled action. Stock RTK types it as fulfilled | rejected, which leaves
// `payload` as `unknown` and forces a cast at every call site.
type FulfilledAction<Returned, ThunkArg> = {
  type: string;
  payload: Returned;
  meta: { arg: ThunkArg; requestId: string; requestStatus: 'fulfilled' };
};

type ThunkPromise<Returned, ThunkArg> = Promise<FulfilledAction<Returned, ThunkArg>> & {
  abort: (reason?: string) => void;
  requestId: string;
  arg: ThunkArg;
  unwrap: () => Promise<Returned>;
};

export interface AppDispatch {
  <Returned, ThunkArg, Config extends object>(
    action: AsyncThunkAction<Returned, ThunkArg, Config>,
  ): ThunkPromise<Returned, ThunkArg>;
  <Returned>(thunk: ThunkAction<Returned, RootState, unknown, UnknownAction>): Returned;
  <A extends Action>(action: A): A;
}

export default store as Omit<typeof store, 'dispatch'> & { dispatch: AppDispatch };
export const history = createReduxHistory(store);
